import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useMitigationTaskNote } from '../../hooks/useMitigationTaskNote'
import { useSuppliers } from '../../hooks/useSuppliers'
import { useSupplierMitigationTasks } from '../../hooks/useSupplierMitigationTasks'
import type { Supplier, SupplierMitigationStatus, SupplierMitigationTask } from '../../types'
import { toErrorMessage } from '../../utils/toErrorMessage'
import { CopyTableButtons } from './CopyTableButtons'
import { cell } from './copyTableToClipboard'
import { EsBadge, EsButton, EsPageHeader } from './ui'
import {
  type ProcurementTypeFilter,
  loadPortfolioFiltersFromStorage,
  matchesProcurementTypeFilter,
  savePortfolioFiltersToStorage,
} from './esPortfolioFilters'
import { ProcurementTypeSelect } from './ProcurementTypeSelect'
import { supplierDisplayName } from './supplierRollup'

const TRACKER_HEADERS = [
  'Supplier',
  'Task',
  'Date',
  'Category',
  'Sub-category',
  'Status',
  'Brand',
] as const

type StatusTab = 'all' | 'open' | 'on_hold' | 'closed'

function normalizeStatus(status: SupplierMitigationStatus): 'open' | 'on_hold' | 'closed' {
  if (status === 'closed' || status === 'completed') return 'closed'
  if (status === 'on_hold' || status === 'scheduled') return 'on_hold'
  return 'open' // open | in_progress | at_risk
}

function statusLabel(status: SupplierMitigationStatus): string {
  const n = normalizeStatus(status)
  if (n === 'on_hold') return 'On-hold'
  if (n === 'closed') return 'Closed'
  return 'Open'
}

function statusBadgeTone(status: SupplierMitigationStatus): 'quiet' | 'riskMedium' | 'default' {
  const n = normalizeStatus(status)
  if (n === 'closed') return 'quiet'
  if (n === 'on_hold') return 'riskMedium'
  return 'default'
}

function severityLabel(t: SupplierMitigationTask): string {
  if (t.severity === 'critical' || t.priority === 'critical') return 'CRITICAL'
  if (t.severity === 'high' || t.priority === 'high') return 'HIGH'
  return 'MEDIUM'
}

function severityTone(t: SupplierMitigationTask): 'riskHigh' | 'riskMedium' | 'alert' {
  const s = severityLabel(t)
  if (s === 'CRITICAL') return 'riskHigh'
  if (s === 'HIGH') return 'alert'
  return 'riskMedium'
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return d
  }
}

function formatSpend(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return `$${n}`
}

export function MitigationTrackerView() {
  const { isAdmin } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const { suppliers, loading: suppliersLoading, error: suppliersError } = useSuppliers(
    isAdmin ? { scope: 'all' } : undefined
  )
  const { tasks, loading: tasksLoading, error: tasksError, updateTask } = useSupplierMitigationTasks()

  const [statusTab, setStatusTab] = useState<StatusTab>('all')
  const [search, setSearch] = useState('')
  const [procurementTypeFilter, setProcurementTypeFilter] = useState<ProcurementTypeFilter>(
    () => loadPortfolioFiltersFromStorage().procurementType
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [commentDraft, setCommentDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const deepLinkHandled = useRef<string | null>(null)

  const supplierMap = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers])

  // Deep-link: /early-signals/mitigation?task=<id>
  useEffect(() => {
    const taskParam = searchParams.get('task')
    if (!taskParam || tasks.length === 0) return
    if (deepLinkHandled.current === taskParam) return
    const match = tasks.find((t) => t.id === taskParam)
    if (!match) return
    deepLinkHandled.current = taskParam
    setStatusTab('all')
    setSearch('')
    setSelectedId(match.id)
    setCommentDraft(match.comments ?? '')
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('task')
        return next
      },
      { replace: true }
    )
  }, [searchParams, tasks, setSearchParams])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = tasks.filter((t) => {
      const supplier = supplierMap.get(t.supplier_id)
      if (!matchesProcurementTypeFilter(supplier?.procurement_type, procurementTypeFilter)) {
        return false
      }
      const n = normalizeStatus(t.status)
      if (statusTab !== 'all' && n !== statusTab) return false
      if (!q) return true
      const hay = `${t.task} ${supplier ? supplierDisplayName(supplier) : ''} ${t.brand ?? ''} ${t.category_manager ?? ''}`.toLowerCase()
      return hay.includes(q)
    })
    return [...list].sort((a, b) => {
      const na = (supplierMap.get(a.supplier_id)?.name || '').trim()
      const nb = (supplierMap.get(b.supplier_id)?.name || '').trim()
      if (!na && !nb) {
        return (a.task || '').localeCompare(b.task || '')
      }
      if (!na) return 1
      if (!nb) return -1
      const byName = na.localeCompare(nb, undefined, { sensitivity: 'base' })
      if (byName !== 0) return byName
      return (a.task || '').localeCompare(b.task || '', undefined, { sensitivity: 'base' })
    })
  }, [tasks, statusTab, search, supplierMap, procurementTypeFilter])

  const trackerRowsAll = useMemo(() => {
    return filtered.map((t) => {
      const supplier = supplierMap.get(t.supplier_id)
      return [
        cell(supplier ? supplierDisplayName(supplier) : ''),
        cell(t.task),
        cell(formatDate(t.alert_date || t.created_at)),
        cell(supplier?.category),
        cell(supplier?.sub_category),
        cell(statusLabel(t.status)),
        cell(t.brand ?? supplier?.brand),
      ]
    })
  }, [filtered, supplierMap])

  const selected = useMemo(
    () => filtered.find((t) => t.id === selectedId) ?? filtered[0] ?? null,
    [filtered, selectedId]
  )

  useEffect(() => {
    if (!selectedId) return
    const el = listRef.current?.querySelector(`[data-task-id="${selectedId}"]`)
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedId, filtered])

  useEffect(() => {
    if (!selected) return
    setCommentDraft(selected.comments ?? '')
  }, [selected?.id]) // eslint-disable-line react-hooks/exhaustive-deps -- sync when selection changes

  const counts = useMemo(() => {
    let open = 0
    let onHold = 0
    let closed = 0
    let all = 0
    for (const t of tasks) {
      const supplier = supplierMap.get(t.supplier_id)
      if (!matchesProcurementTypeFilter(supplier?.procurement_type, procurementTypeFilter)) continue
      all++
      const n = normalizeStatus(t.status)
      if (n === 'open') open++
      else if (n === 'on_hold') onHold++
      else closed++
    }
    return { all, open, on_hold: onHold, closed }
  }, [tasks, supplierMap, procurementTypeFilter])

  const saveStatus = async (status: 'open' | 'on_hold' | 'closed') => {
    if (!selected) return
    await updateTask({ id: selected.id, status })
  }

  const saveComments = async () => {
    if (!selected) return
    await updateTask({ id: selected.id, comments: commentDraft })
  }

  const loading = suppliersLoading || tasksLoading
  const error = suppliersError || tasksError

  return (
    <div className="p-4 md:p-6">
      <EsPageHeader
        className="mb-3"
        title="Mitigation tracker"
        description="Alerts with ownership, status, and next steps"
        eyebrow="Actions"
      />
      <ProcurementTypeSelect
        value={procurementTypeFilter}
        onChange={(next) => {
          setProcurementTypeFilter(next)
          savePortfolioFiltersToStorage({
            ...loadPortfolioFiltersFromStorage(),
            procurementType: next,
          })
        }}
        className="mb-4"
      />

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm mb-4">
          {toErrorMessage(error, 'Failed to load mitigation data.')}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-[min(720px,calc(100vh-11rem))] min-h-[420px]">
        <div className="lg:col-span-2 bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] flex flex-col overflow-hidden min-h-0">
          <div className="p-3 border-b border-es-soft space-y-2 shrink-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-es-muted">
                Mitigation alerts
              </span>
              <CopyTableButtons
                disabled={loading || trackerRowsAll.length === 0}
                payload={{ headers: [...TRACKER_HEADERS], rows: trackerRowsAll }}
              />
            </div>
            <input
              type="search"
              placeholder="Search alerts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-[var(--radius-sm)] border border-es-hairline px-3 py-2 text-sm text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal"
            />
            <div className="flex flex-wrap gap-1">
              {(
                [
                  ['all', 'All'],
                  ['open', 'Open'],
                  ['on_hold', 'On-hold'],
                  ['closed', 'Closed'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setStatusTab(id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    statusTab === id
                      ? 'bg-es-signal text-white border-es-signal'
                      : 'bg-es-white text-es-body border-es-hairline hover:border-es-signal'
                  }`}
                >
                  {label} ({counts[id]})
                </button>
              ))}
            </div>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="p-6 text-center text-es-muted text-sm">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-es-muted text-sm">No mitigation alerts yet.</div>
            ) : (
              <ul className="divide-y divide-es-soft">
                {filtered.map((t) => {
                  const supplier = supplierMap.get(t.supplier_id)
                  const active = selected?.id === t.id
                  return (
                    <li key={t.id} data-task-id={t.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedId(t.id)
                          setCommentDraft(t.comments ?? '')
                        }}
                        className={`w-full text-left px-3 py-3 hover:bg-es-haze ${
                          active ? 'bg-es-haze border-l-4 border-l-es-signal' : 'border-l-4 border-l-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold text-es-navy text-sm truncate">
                            {supplier ? supplierDisplayName(supplier) : 'Unknown supplier'}
                          </span>
                          <span className="text-xs text-es-muted shrink-0">{formatDate(t.alert_date || t.created_at)}</span>
                        </div>
                        <p className="text-sm text-es-body mt-0.5 line-clamp-2">{t.task}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span className="text-xs text-es-muted">
                            {[supplier?.category, supplier?.sub_category].filter(Boolean).join(' / ') || '—'}
                          </span>
                          <EsBadge tone={statusBadgeTone(t.status)} className="!text-[10px] !px-1.5 !py-0.5">
                            {statusLabel(t.status)}
                          </EsBadge>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-3 bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-5 overflow-y-auto min-h-0">
          {!selected ? (
            <p className="text-es-muted text-sm">Select an alert to view details.</p>
          ) : (
            <AlertDetail
              task={selected}
              supplier={supplierMap.get(selected.supplier_id)}
              commentDraft={commentDraft}
              setCommentDraft={setCommentDraft}
              onSaveComments={saveComments}
              onStatus={saveStatus}
              isAdmin={!!isAdmin}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function AlertDetail({
  task,
  supplier,
  commentDraft,
  setCommentDraft,
  onSaveComments,
  onStatus,
  isAdmin,
}: {
  task: SupplierMitigationTask
  supplier?: Supplier
  commentDraft: string
  setCommentDraft: (v: string) => void
  onSaveComments: () => void
  onStatus: (s: 'open' | 'on_hold' | 'closed') => void
  isAdmin: boolean
}) {
  const privateNote = useMitigationTaskNote(task.id)

  const copyPayload = useMemo(() => {
    const sharedNotes = (commentDraft || task.comments || '').trim() || '—'
    const rows: string[][] = [
      ['Supplier', cell(supplier ? supplierDisplayName(supplier) : '')],
      ['Task', cell(task.task)],
      ['Severity', severityLabel(task)],
      ['Status', statusLabel(task.status)],
      ['Supplier ID', cell(supplier?.supplier_unique_id)],
      ['Plant Location', cell(task.plant_location || supplier?.location)],
      ['Category', cell(supplier?.category || supplier?.industry)],
      ['Sub-category', cell(supplier?.sub_category)],
      ['Spend', formatSpend(task.spend)],
      ['Brand', cell(task.brand || supplier?.brand)],
      ['Category Manager', cell(task.category_manager)],
      ['Regional Cat Head', cell(task.regional_head)],
      ['Date', formatDate(task.alert_date || task.created_at)],
      ['Shared notes', sharedNotes],
    ]
    return {
      headers: ['Field', 'Value'],
      rows,
      preamble: [
        `${supplier ? supplierDisplayName(supplier) : 'Supplier'} · ${statusLabel(task.status)}`,
        task.task,
      ].filter(Boolean),
    }
  }, [task, supplier, commentDraft])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <EsBadge tone={severityTone(task)} className="!text-xs !px-2 !py-0.5">
            {severityLabel(task)}
          </EsBadge>
          <h2 className="text-xl font-bold text-es-navy mt-2">
            {supplier ? supplierDisplayName(supplier) : 'Supplier'}
          </h2>
          <p className="text-sm text-es-body mt-1">{task.task}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <CopyTableButtons payload={copyPayload} />
          {isAdmin && (
            <select
              value={normalizeStatus(task.status)}
              onChange={(e) => onStatus(e.target.value as 'open' | 'on_hold' | 'closed')}
              className="rounded-[var(--radius-sm)] border border-es-hairline px-2 py-1.5 text-sm text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal"
              aria-label="Status"
            >
              <option value="open">Open</option>
              <option value="on_hold">On-hold</option>
              <option value="closed">Closed</option>
            </select>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <dt className="text-es-muted">Supplier ID</dt>
          <dd className="font-medium text-es-navy">{supplier?.supplier_unique_id ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Plant Location</dt>
          <dd className="font-medium text-es-navy">
            {task.plant_location || supplier?.location || '—'}
          </dd>
        </div>
        <div>
          <dt className="text-es-muted">Category</dt>
          <dd className="font-medium text-es-navy">{supplier?.category || supplier?.industry || '—'}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Sub-category</dt>
          <dd className="font-medium text-es-navy">{supplier?.sub_category ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Spend</dt>
          <dd className="font-medium text-es-navy">{formatSpend(task.spend)}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Brand</dt>
          <dd className="font-medium text-es-navy">{task.brand || supplier?.brand || '—'}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Category Manager</dt>
          <dd className="font-medium text-es-navy">{task.category_manager ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Regional Cat Head</dt>
          <dd className="font-medium text-es-navy">{task.regional_head ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Date</dt>
          <dd className="font-medium text-es-navy">{formatDate(task.alert_date || task.created_at)}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Status</dt>
          <dd>
            <EsBadge tone={statusBadgeTone(task.status)} className="!text-xs !px-1.5 !py-0.5">
              {statusLabel(task.status)}
            </EsBadge>
          </dd>
        </div>
      </dl>

      <div>
        <h3 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">Shared notes</h3>
        {isAdmin ? (
          <div className="space-y-2">
            <textarea
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              rows={3}
              className="w-full rounded-[var(--radius-sm)] border border-es-hairline px-3 py-2 text-sm text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal"
              placeholder="Team commentary / imported notes..."
            />
            <EsButton type="button" onClick={onSaveComments} className="!px-3 !py-1.5 !text-sm">
              Save shared notes
            </EsButton>
          </div>
        ) : (
          <p className="text-sm text-es-body whitespace-pre-wrap">{task.comments || '—'}</p>
        )}
      </div>

      {privateNote.enabled && (
        <div>
          <h3 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">My notes</h3>
          <p className="text-xs text-es-muted mb-2">Only you can see these.</p>
          <div className="space-y-2">
            <textarea
              value={privateNote.body}
              onChange={(e) => privateNote.setBody(e.target.value)}
              rows={3}
              disabled={privateNote.loading}
              className="w-full rounded-[var(--radius-sm)] border border-es-hairline px-3 py-2 text-sm text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal"
              placeholder="Private notes for this alert..."
            />
            <EsButton
              type="button"
              onClick={() => void privateNote.save()}
              disabled={privateNote.saving || privateNote.loading}
              className="!px-3 !py-1.5 !text-sm"
            >
              {privateNote.saving ? 'Saving…' : 'Save my notes'}
            </EsButton>
            {privateNote.error && (
              <p className="text-xs text-red-700">
                {privateNote.error instanceof Error
                  ? privateNote.error.message
                  : String(privateNote.error)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
